import { buildOrganizationUrn, organizationUrnPrefix } from '@logto/core-kit';
import {
  defaultTenantId,
  getManagementApiResourceIndicator,
  getTenantOrganizationId,
} from '@logto/schemas';
import { appendPath } from '@silverhand/essentials';

type Input = {
  isCloud: boolean;
  isMultiTenant: boolean;
  currentTenantId: string;
  tenantEndpoint?: URL;
  origin: string;
};

type Output = {
  prefixUrl: URL;
  resourceIndicator: string;
};

/**
 * Pure helper for the `useApi` hook — resolves the per-mode prefix URL and resource indicator.
 *
 * - Cloud: path-based proxy at `{origin}/m/:tenantId` with an organization-URN resource.
 * - Self-hosted multi-tenant: same path-based proxy with the per-tenant organization URN.
 *   Tenant organizations are created on tenant creation (and backfilled on startup) so the
 *   console can acquire organization tokens for any tenant the user is a member of.
 * - Single-tenant OSS: the tenant's own Management API endpoint.
 *
 * Throws in single-tenant OSS when asked for a non-default tenant, since those grants don't
 * exist in that deployment mode.
 */
export const resolveApiConfig = ({
  isCloud,
  isMultiTenant,
  currentTenantId,
  tenantEndpoint,
  origin,
}: Input): Output => {
  if (isCloud || isMultiTenant) {
    return {
      prefixUrl: appendPath(new URL(origin), 'm', currentTenantId),
      resourceIndicator: buildOrganizationUrn(getTenantOrganizationId(currentTenantId)),
    };
  }

  if (currentTenantId !== defaultTenantId) {
    throw new Error('Only the default tenant is supported in OSS.');
  }

  if (!tenantEndpoint) {
    throw new Error('Single-tenant OSS is missing the tenant endpoint.');
  }

  return {
    prefixUrl: tenantEndpoint,
    resourceIndicator: getManagementApiResourceIndicator(currentTenantId),
  };
};

export const isOrganizationResource = (resourceIndicator: string): boolean =>
  resourceIndicator.startsWith(organizationUrnPrefix);
